import { FullPageSpinner } from 'app/common/daisy/full-page-spinner';
import Form from 'app/schema-form/components/schema-form';
import { LoaderButton } from '../../../common/daisy/loader-button.js';
import { NO_DATA_SOURCE } from '../../agent-eval-store.js';
import type { AgentEvalRunEditProps } from './agent-eval-edit-run-hook.js';
import { config, EVAL_FORM_NAME, type EvalFormState, schema, widgets } from './eval-form.js';

export const AgentEvalEditRun = (props: AgentEvalRunEditProps) => {
  const {
    loading,
    saving,
    initialData,
    runEditOpen,
    closeModal,
    handleSubmit,
    dataSourceOptions,
    dataSource,
    setDataSource,
    resetEval,
    runEval,
    isRunning,
    evalResults,
    validator,
    sessionId,
    setSessionId
  } = props;

  let content: React.ReactNode;
  if (loading) {
    content = <FullPageSpinner />;
  } else {
    content = (
      <div className="flex flex-1 min-h-0">
        {/* Left Column - Eval Editor */}
        <div className="flex-1 border-r border-base-300 pr-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-md">Edit Eval</h4>
            <LoaderButton loading={saving} className="btn" onClick={handleSubmit}>
              {saving ? 'Saving' : 'Save'}
            </LoaderButton>
          </div>

          <Form<EvalFormState>
            formName={EVAL_FORM_NAME}
            schema={schema}
            config={config}
            widgets={widgets}
            initialData={initialData}
            validator={validator}
          />
        </div>

        {/* Right Column - Eval Runner */}
        <div className="flex-1 pl-4 flex flex-col">
          <div className="flex items-center gap-2 justify-between mb-2">
            <span className="font-semibold text-md text-nowrap">Run Eval</span>
            <select className="select select-sm" value={dataSource.id} onChange={(e) => setDataSource(e.target.value)}>
              <option value={NO_DATA_SOURCE.id}>Select data source...</option>
              {dataSourceOptions?.map((dataSource) => (
                <option key={dataSource.id} value={dataSource.id}>
                  {dataSource.type}
                  {'name' in dataSource ? `: ${dataSource.name}` : ``}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-ghost" onClick={resetEval}>
              Clear
            </button>
            <button
              type="button"
              className={`btn`}
              onClick={() => {
                runEval();
              }}
              disabled={isRunning || !dataSource || dataSource.id === NO_DATA_SOURCE.id}
            >
              Run
            </button>
          </div>

          {dataSource.type === 'Session ID' && (
            <div className="mb-2">
              <input
                type="text"
                className="input input-sm w-full"
                placeholder="Enter session ID..."
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
              />
            </div>
          )}

          <div className="flex-1 bg-base-200 rounded-lg p-4 overflow-auto min-h-0">
            {isRunning ? (
              <div className="text-sm">
                <p className="mb-3">Eval running...</p>
              </div>
            ) : evalResults ? (
              <div className="text-sm space-y-4">{evalResults.result}</div>
            ) : (
              <div className="text-sm">
                <p>Results will appear here when you run the eval.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    runEditOpen && (
      <div className="modal modal-open">
        <div className="modal-box max-w-6xl w-11/12 h-5/6 max-h-none flex flex-col">
          {content}
          <div className="modal-action mt-4">
            <button type="button" className="btn" onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
        <div className="modal-backdrop" onClick={closeModal} />
      </div>
    )
  );
};
